import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Modal from "./Modal.jsx";
import useFamiliaStatus from "../hooks/useFamiliaStatus.js";
import { getBabaPhotoCandidates, handleBabaPhotoError, BABA_PLACEHOLDER } from "../utils/babaPhoto.js";
import { Check, Loader2 } from "lucide-react";

// Planos ativos no Mercado Pago (links prontos mpago.la).
const PLANOS = [
  { nome: "Essencial", valor: "R$ 9,90", link: "https://mpago.la/1DbVRAm" },
  { nome: "Confort", valor: "R$ 19,90", link: "https://mpago.la/2x24eSj", destaque: true },
  { nome: "Premium", valor: "R$ 39,90", link: "https://mpago.la/2qRxHCA" },
];

const btnPrimary =
  "w-full rounded-xl bg-[var(--color-rose-400)] py-2.5 text-sm font-semibold text-white hover:brightness-105 active:scale-95 transition disabled:opacity-60 disabled:cursor-not-allowed";

function BabaResumo({ baba }) {
  const foto = getBabaPhotoCandidates(baba?.foto_url)[0] || BABA_PLACEHOLDER;
  return (
    <div className="flex items-center gap-3 mb-4">
      <img
        src={foto}
        alt={baba?.nome}
        className="h-14 w-14 rounded-xl object-cover shrink-0"
        onError={(e) => handleBabaPhotoError(e, getBabaPhotoCandidates(baba?.foto_url))}
      />
      <div className="min-w-0">
        <p className="font-bold text-warm-900 leading-tight">
          {baba?.nome} {baba?.sobrenome || ""}
        </p>
        {baba?.cidade && <p className="text-xs text-warm-400">{baba.cidade}</p>}
      </div>
    </div>
  );
}

export default function BotaoContratacao({ baba, className = "" }) {
  const navigate = useNavigate();
  const { status, loading, familiaId } = useFamiliaStatus();

  const [open, setOpen] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState("");

  // Babá/admin logados não usam o paywall de família.
  if (loading || status === "nao_familia") return null;

  const irParaCadastro = () => {
    // Guarda a origem para voltar ao perfil após o login (Fase 6.2).
    try {
      sessionStorage.setItem("redirect_after_login", window.location.pathname);
    } catch {}
    navigate("/auth-options");
  };

  const confirmarSolicitacao = async () => {
    setErro("");
    setEnviando(true);
    const { error } = await supabase.from("solicitacoes_familia").insert({
      familia_id: familiaId,
      baba_id: baba?.id,
      mensagem: mensagem.trim() || null,
    });
    setEnviando(false);
    if (error) {
      setErro("Não foi possível enviar sua solicitação. Tente novamente.");
      return;
    }
    setSucesso(true);
  };

  const fechar = () => {
    setOpen(false);
    // Reset após a animação de fechamento.
    setTimeout(() => {
      setMensagem("");
      setSucesso(false);
      setErro("");
    }, 200);
  };

  const tituloModal =
    status === "visitante"
      ? "Crie sua conta para continuar"
      : status === "familia_cadastrada"
      ? "Ative sua assinatura Tesourinho's"
      : "Solicitar contratação";

  return (
    <>
      <button className={`${btnPrimary} ${className}`} onClick={() => setOpen(true)}>
        Solicitar contratação
      </button>

      <Modal isOpen={open} onClose={fechar} title={tituloModal}>
        {/* VISITANTE — precisa criar conta */}
        {status === "visitante" && (
          <div>
            <p className="text-sm text-warm-600 leading-relaxed mb-5">
              Crie sua conta gratuitamente para solicitar a contratação de uma babá.
              É rápido e sem compromisso.
            </p>
            <button className={btnPrimary} onClick={irParaCadastro}>
              Criar conta
            </button>
          </div>
        )}

        {/* FAMÍLIA SEM ASSINATURA — paywall */}
        {status === "familia_cadastrada" && (
          <div>
            <p className="text-sm text-warm-600 leading-relaxed mb-4">
              Para contratar uma babá, ative sua assinatura Tesourinho's. Escolha o
              plano ideal para a sua família:
            </p>
            <div className="space-y-2.5">
              {PLANOS.map((p) => (
                <a
                  key={p.nome}
                  href={p.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-between rounded-xl border px-4 py-3 transition hover:border-[var(--color-rose-400)] hover:bg-rose-400/5 ${
                    p.destaque
                      ? "border-[var(--color-rose-400)] bg-rose-400/5"
                      : "border-warm-200"
                  }`}
                >
                  <span className="flex flex-col">
                    <span className="text-sm font-bold text-warm-900">
                      {p.nome}
                      {p.destaque && (
                        <span className="ml-2 rounded-full bg-[var(--color-rose-400)] px-2 py-0.5 text-[10px] font-semibold text-white">
                          Popular
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-warm-400">{p.valor}/mês</span>
                  </span>
                  <span className="text-sm font-semibold text-[var(--color-rose-400)]">
                    Assinar →
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* FAMÍLIA ASSINANTE — confirmação da solicitação */}
        {status === "familia_assinante" && !sucesso && (
          <div>
            <BabaResumo baba={baba} />
            <p className="text-sm text-warm-600 leading-relaxed mb-4">
              Sua solicitação será analisada pela equipe Tesourinho's, que entrará em
              contato em breve. Nós intermediamos todo o contato com a babá.
            </p>
            <label className="block text-xs font-semibold text-warm-600 mb-1.5">
              Conte um pouco sobre sua necessidade (opcional)
            </label>
            <textarea
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              rows={3}
              placeholder="Ex.: preciso de ajuda nas tardes de segunda a sexta…"
              className="w-full rounded-xl border border-warm-200 px-3 py-2 text-sm text-warm-800 placeholder:text-warm-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-rose-400)]/40 resize-none mb-4"
            />
            {erro && <p className="text-xs text-red-500 mb-3">{erro}</p>}
            <button className={btnPrimary} onClick={confirmarSolicitacao} disabled={enviando}>
              {enviando ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Enviando…
                </span>
              ) : (
                "Confirmar solicitação"
              )}
            </button>
          </div>
        )}

        {/* SUCESSO */}
        {status === "familia_assinante" && sucesso && (
          <div className="text-center py-2">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Check size={24} />
            </div>
            <p className="text-sm font-semibold text-warm-900 mb-1">
              Solicitação enviada!
            </p>
            <p className="text-sm text-warm-500 mb-5">
              Nossa equipe entrará em contato.
            </p>
            <button className={btnPrimary} onClick={fechar}>
              Fechar
            </button>
          </div>
        )}
      </Modal>
    </>
  );
}
